@if (canPage()) {
  <lib-timeline-nav
    unit="week"
    [title]="navTitle()"
    [atFirst]="atFirst()"
    [atLast]="atLast()"
    (today)="goToday()"
    (step)="step($event)"
  />
}
<div class="scroll">
  <div
    #chart
    class="gantt"
    role="region"
    [attr.aria-label]="label()"
  >
    <div
      class="head"
      [class.banded]="scale() !== 'days'"
      [style.grid-template-columns]="gridColumns()"
      aria-hidden="true"
    >
      <div class="corner">{{ cornerLabel() }}</div>
      @if (scale() === "days") {
        @for (day of days(); track day.day) {
          <div
            class="day"
            [class.weekend]="day.weekend"
            [style.grid-column]="day.column"
          >
            <span>{{ day.weekday }}</span>
            <span
              class="day-num"
              [class.today]="day.today"
              >{{ day.date }}</span
            >
          </div>
        }
      } @else {
        @for (band of monthBands(); track band.key) {
          <div
            class="band band-month"
            [style.grid-row]="1"
            [style.grid-column]="band.column + ' / span ' + band.span"
          >
            {{ band.text }}
          </div>
        }
        @for (run of weekendRuns(); track run.key) {
          <div
            class="weekend-shade head-shade"
            [style.grid-row]="2"
            [style.grid-column]="run.column + ' / span ' + run.span"
          ></div>
        }
        @for (band of weekBands(); track band.key) {
          <div
            class="band band-week"
            [class.current]="band.current"
            [style.grid-row]="2"
            [style.grid-column]="band.column + ' / span ' + band.span"
          >
            {{ band.text }}
          </div>
        }
      }
    </div>

    @for (view of rowViews(); track view.row.id) {
      <div
        class="row"
        [class.group]="view.row.isGroup"
        role="group"
        [attr.aria-label]="view.name + ', ' + view.row.sub"
        [style.grid-template-columns]="gridColumns()"
        [style.grid-template-rows]="view.gridRows"
      >
        <div
          class="label"
          [style.grid-row]="'1 / ' + view.rowEnd"
        >
          @if (view.row.detailId; as detailId) {
            <button
              type="button"
              class="label-text label-btn"
              [class.is-selected]="selectedId() === detailId"
              [attr.aria-pressed]="selectedId() === detailId"
              [attr.aria-label]="view.name + ', ' + view.row.sub + ', details'"
              (click)="entrySelected.emit(detailId)"
            >
              <ng-container [ngTemplateOutlet]="labelText" />
            </button>
          } @else {
            <span class="label-text">
              <ng-container [ngTemplateOutlet]="labelText" />
            </span>
          }
          <ng-template #labelText>
            <span
              class="label-main"
              [title]="view.name"
              >{{ view.row.label }}
              @if (view.row.labelSuffix; as suffix) {
                <span class="label-suffix">{{ suffix }}</span>
              }
            </span>
            <span
              class="label-sub"
              [title]="view.row.sub"
              >{{ view.row.sub }}</span
            >
          </ng-template>
          @if (view.row.warning; as warning) {
            <button
              mat-icon-button
              type="button"
              class="warn-btn"
              [class.caution]="warning.level === 'warning'"
              [attr.aria-label]="warning.label"
              [title]="warning.label"
              (click)="entrySelected.emit(warning.detailId)"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                aria-hidden="true"
              >
                <path d="M12 2 1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2V9h2v4z" />
              </svg>
            </button>
          }
        </div>
        @if (scale() === "days") {
          @for (day of days(); track day.day) {
            <div
              class="bg"
              [class.weekend]="day.weekend"
              [class.today]="day.today"
              [style.grid-column]="day.column"
              [style.grid-row]="'1 / ' + view.rowEnd"
            ></div>
          }
        } @else {
          @for (band of weekBands(); track band.key) {
            <div
              class="bg"
              [style.grid-column]="band.column + ' / span ' + band.span"
              [style.grid-row]="'1 / ' + view.rowEnd"
            ></div>
          }
          @for (run of weekendRuns(); track run.key) {
            <div
              class="weekend-shade"
              [style.grid-column]="run.column + ' / span ' + run.span"
              [style.grid-row]="'1 / ' + view.rowEnd"
            ></div>
          }
          @if (todayColumn(); as column) {
            <div
              class="today-line"
              [class]="'today-' + todayStyle()"
              [style.grid-column]="column"
              [style.grid-row]="'1 / ' + view.rowEnd"
            ></div>
          }
        }
        @for (guide of view.guides; track guide.id) {
          <div
            class="guide"
            [class.from-top]="guide.fromLane === 1 && !view.row.isGroup"
            [style.--line-color]="guide.color"
            [style.grid-column]="guide.column"
            [style.grid-row]="guide.fromLane + ' / ' + view.rowEnd"
          ></div>
        }
        @for (mark of view.marks; track mark.id) {
          <div
            class="mark"
            [style.background]="mark.color"
            [style.grid-column]="mark.column"
            [style.grid-row]="mark.lane"
          ></div>
        }
        @for (bar of view.bars; track bar.entry.id) {
          <lib-timeline-bar
            [bar]="bar"
            [selected]="selectedId() === bar.entry.id"
            [style.grid-column]="bar.firstColumn + 1 + ' / ' + (bar.lastColumn + 2)"
            [style.grid-row]="bar.lane + 1"
            (selectEntry)="entrySelected.emit($event)"
          />
        }
        @for (cell of view.cells; track cell.column) {
          <div
            class="chips"
            [style.grid-column]="cell.column"
            [style.grid-row]="view.rowEnd - 1"
          >
            @for (entry of cell.entries; track entry.id) {
              <lib-timeline-chip
                [entry]="entry"
                [compact]="compact()"
                [selected]="selectedId() === entry.id"
                (selectEntry)="entrySelected.emit($event)"
              />
            }
          </div>
        }
        @for (item of view.items; track item.entry.id) {
          @if (item.bar; as bar) {
            <lib-timeline-bar
              class="packed-bar"
              [bar]="bar"
              [style.margin-left.px]="bar.continuesLeft ? 0 : item.startHalfPx - 4"
              [style.margin-right.px]="bar.continuesRight ? 0 : item.endHalfPx - 4"
              [selected]="selectedId() === item.entry.id"
              [style.grid-column]="item.column + ' / span ' + item.span"
              [style.grid-row]="item.lane"
              (selectEntry)="entrySelected.emit($event)"
            />
          } @else if (dateStyle() === "line") {
            <button
              type="button"
              class="pill"
              [class.pill-tint]="item.entry.tint"
              [style.--entry-color]="item.entry.color"
              [class.is-selected]="selectedId() === item.entry.id"
              [attr.aria-label]="entryAccessibleName(item.entry)"
              [attr.aria-pressed]="selectedId() === item.entry.id"
              [title]="entryAccessibleName(item.entry)"
              [style.grid-column]="item.column + ' / span ' + item.span"
              [style.grid-row]="item.lane"
              [style.margin-left.px]="item.startHalfPx + 4"
              (click)="entrySelected.emit(item.entry.id)"
            >
              @if (item.entry.warn) {
                <svg
                  class="pill-warn"
                  [class.caution]="item.entry.warn === 'warning'"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M12 2 1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2V9h2v4z" />
                </svg>
              }
              <span
                class="pill-text"
                aria-hidden="true"
                >{{ item.text }}</span
              >
            </button>
          } @else {
            <lib-timeline-chip
              class="packed-chip"
              [entry]="item.entry"
              [text]="item.text"
              [compact]="true"
              [selected]="selectedId() === item.entry.id"
              [style.grid-column]="item.column + ' / span ' + item.span"
              [style.grid-row]="item.lane"
              (selectEntry)="entrySelected.emit($event)"
            />
          }
        }
      </div>
    } @empty {
      <p class="empty">{{ emptyText() }}</p>
    }
  </div>
</div>
